import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Play, Shield, GraduationCap, Bot, Wrench, CheckCircle2, Sparkles, MessageCircle, ImageIcon, Video, Type, Search, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { TranslatedText } from '@/components/TranslatedText';

const pillars = [
  {
    icon: GraduationCap,
    number: "01",
    title: "Learn the Systems Behind Faceless Online Businesses",
    subtitle: "Structured lessons that show you how real faceless content channels, traffic sites, and digital projects are built.",
    description: "Each module teaches a practical skill — from choosing a profitable niche to publishing your first content and building automated systems. Lessons are short, focused, and designed so you can apply what you learn immediately.",
    highlights: [
      "Step-by-step lessons focused on real workflows",
      "Learn how to choose niches, create content, and grow traffic",
      "Short quizzes that reinforce what actually matters",
      "Track your progress across multiple online business models",
    ],
  },
  {
    icon: Bot,
    number: "02",
    title: "Your AI Personal Teacher",
    subtitle: "Guidance whenever you need it.",
    description: "Building online projects can feel confusing at first. Your AI teacher is available 24/7 to answer questions, explain concepts, and guide you through each step of your project. Think of it as a mentor that helps you move forward whenever you get stuck.",
    highlights: [
      "Ask questions about any lesson or project",
      "Get guidance based on your specific niche or idea",
      "Get help planning content, scripts, and strategies",
      "Available anytime — no waiting, no scheduling",
    ],
  },
  {
    icon: Wrench,
    number: "03",
    title: "Build While You Learn",
    subtitle: "Turn ideas into real online assets.",
    description: "As you go through the lessons, you can immediately apply what you learn using built-in AI tools designed for content creation, research, and strategy. Everything is integrated directly into the platform so you can focus on building instead of juggling multiple tools.",
    tools: [
      { icon: ImageIcon, name: "AI Image Creator", use: "Generate visuals for posts, thumbnails, and content pages" },
      { icon: Video, name: "Video Script Generator", use: "Create ready-to-record scripts with hook, structure, and CTA" },
      { icon: Type, name: "Caption & Hook Writer", use: "Write captions designed to capture attention" },
      { icon: Search, name: "Niche Analyzer", use: "Validate niche ideas using competition and monetization signals" },
      { icon: CalendarDays, name: "Content Strategy Planner", use: "Generate a 30-day content roadmap for your project" },
    ],
  },
];

export const HeroMediaSlideshow = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} id="solution" className="py-14 sm:py-24 px-4 bg-gradient-to-b from-background to-muted/30">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="text-sm font-medium text-muted-foreground tracking-widest uppercase mb-3 block">
            <TranslatedText>How It Works</TranslatedText>
          </span>
          <p className="text-xs font-semibold text-primary tracking-wider uppercase mb-4">
            <TranslatedText>A Structured System — Not a Random Course</TranslatedText>
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3">
            <TranslatedText>Learn. Build. Monetize.</TranslatedText>
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
            <TranslatedText>A clear system that guides you from your first lesson to launching your first real online project.</TranslatedText>
          </p>
          <p className="text-xs font-semibold text-muted-foreground/70 tracking-widest uppercase mt-3">
            Content · Traffic · Ecommerce · Automation
          </p>
        </motion.div>

        {/* Pillars */}
        <div className="space-y-6 mb-12">
          {pillars.map((pillar, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + index * 0.15 }}
              className="rounded-2xl bg-card border border-border overflow-hidden"
            >
              {/* Pillar Header */}
              <div className="flex items-center gap-3 p-5 pb-3">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <pillar.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <span className="text-xs font-bold text-primary">{pillar.number}</span>
                  <h3 className="text-base sm:text-lg font-bold text-foreground leading-tight">
                    <TranslatedText>{pillar.title}</TranslatedText>
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    <TranslatedText>{pillar.subtitle}</TranslatedText>
                  </p>
                </div>
              </div>

              {/* Pillar Body */}
              <div className="px-5 pb-5">
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  <TranslatedText>{pillar.description}</TranslatedText>
                </p>

                {/* Highlights for pillars 1 & 2 */}
                {pillar.highlights && (
                  <ul className="space-y-2">
                    {pillar.highlights.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span><TranslatedText>{item}</TranslatedText></span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Tools grid for pillar 3 */}
                {pillar.tools && (
                  <div className="space-y-2.5">
                    {pillar.tools.map((tool, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-muted/50 border border-border/50">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <tool.icon className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground leading-tight">
                            <TranslatedText>{tool.name}</TranslatedText>
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            <TranslatedText>{tool.use}</TranslatedText>
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-center"
        >
          <Link to="/apply?plan=free">
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground h-14 px-8 text-base font-semibold rounded-xl active:scale-[0.98] shadow-lg"
            >
              <Play className="w-5 h-5 mr-2" />
              <TranslatedText>Start With Your First Free Class</TranslatedText>
            </Button>
          </Link>
          <div className="flex items-center justify-center gap-1.5 mt-4 text-muted-foreground">
            <Shield className="w-3.5 h-3.5" />
            <span className="text-xs">
              <TranslatedText>No credit card required</TranslatedText>
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroMediaSlideshow;
